import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminHeader from './AdminHeader';
import { Drawer } from '../shared/DrawerManager';
import toastr from 'toastr';
import './TeamsIntegration.css';

const TeamsIntegration = ({ onNavigate, currentView }) => {
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Teams Configuration State
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [notifications, setNotifications] = useState({
    newApplication: true,
    interviewScheduled: true,
    assessmentCompleted: true,
    challengeSubmitted: false,
    systemAlerts: true
  });

  // Microsoft Graph Configuration State
  const [graphConfig, setGraphConfig] = useState({
    enabled: false,
    tenantId: '',
    clientId: '',
    clientSecret: '',
    organizerEmail: ''
  });
  const [notificationMode, setNotificationMode] = useState('channel');

  // UI State
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingGraphConnection, setTestingGraphConnection] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const [showGraphHelpDrawer, setShowGraphHelpDrawer] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/integrations/teams', {
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setWebhookUrl(data.config.webhookUrl || '');
          setIsEnabled(data.config.isEnabled || false);
          setNotifications(data.config.notifications || notifications);

          // Load Microsoft Graph config
          if (data.config.microsoftGraph) {
            setGraphConfig({
              enabled: data.config.microsoftGraph.enabled || false,
              tenantId: data.config.microsoftGraph.tenantId || '',
              clientId: data.config.microsoftGraph.clientId || '',
              clientSecret: data.config.microsoftGraph.clientSecret || '',
              organizerEmail: data.config.microsoftGraph.organizerEmail || ''
            });
          }
          setNotificationMode(data.config.notificationMode || 'channel');
        }
      }
    } catch (err) {
      console.error('Failed to load Teams configuration:', err);
      // Don't show error for first-time setup
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      if (isEnabled && !webhookUrl) {
        toastr.error('Please provide a Teams webhook URL');
        return;
      }

      if (webhookUrl && !webhookUrl.startsWith('https://')) {
        toastr.error('Webhook URL must start with https://');
        return;
      }

      const response = await fetch('/api/integrations/teams', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        },
        body: JSON.stringify({
          webhookUrl,
          isEnabled,
          notifications
        })
      });

      const data = await response.json();

      if (response.ok) {
        toastr.success('Teams integration configured successfully!');
      } else {
        throw new Error(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save Teams configuration:', err);
      toastr.error(err.message || 'Failed to save configuration');
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);

      if (!webhookUrl) {
        toastr.error('Please provide a webhook URL to test');
        return;
      }

      const response = await fetch('/api/integrations/teams/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        },
        body: JSON.stringify({ webhookUrl })
      });

      const data = await response.json();

      if (response.ok) {
        toastr.success('Test message sent successfully! Check your Teams channel.');
      } else {
        throw new Error(data.error || 'Failed to send test message');
      }
    } catch (err) {
      console.error('Failed to test Teams connection:', err);
      toastr.error(err.message || 'Failed to send test message');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveGraphConfig = async () => {
    try {
      // Validate Graph configuration if enabled
      if (graphConfig.enabled) {
        if (!graphConfig.tenantId || !graphConfig.clientId || !graphConfig.clientSecret) {
          toastr.error('Please provide Tenant ID, Client ID, and Client Secret for Microsoft Graph');
          return;
        }
        if (!graphConfig.organizerEmail) {
          toastr.error('Please provide an organizer email for calendar events');
          return;
        }
      }

      const response = await fetch('/api/integrations/teams/graph', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        },
        body: JSON.stringify({
          microsoftGraph: graphConfig,
          notificationMode
        })
      });

      const data = await response.json();

      if (response.ok) {
        toastr.success('Microsoft Graph configuration saved successfully!');
      } else {
        throw new Error(data.error || 'Failed to save Graph configuration');
      }
    } catch (err) {
      console.error('Failed to save Graph configuration:', err);
      toastr.error(err.message || 'Failed to save Graph configuration');
    }
  };

  const handleTestGraphConnection = async () => {
    try {
      setTestingGraphConnection(true);

      if (!graphConfig.tenantId || !graphConfig.clientId || !graphConfig.clientSecret) {
        toastr.error('Please provide all Graph API credentials to test');
        setTestingGraphConnection(false);
        return;
      }

      const response = await fetch('/api/integrations/teams/graph/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        }
      });

      const data = await response.json();

      if (response.ok) {
        toastr.success(`Graph API connected successfully! Organization: ${data.organizationName}`);
      } else {
        throw new Error(data.error || 'Failed to connect to Microsoft Graph');
      }
    } catch (err) {
      console.error('Failed to test Graph connection:', err);
      toastr.error(err.message || 'Failed to test Graph connection');
    } finally {
      setTestingGraphConnection(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="teams-integration">
        <div className="error-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="teams-integration">
        <AdminHeader currentView={currentView} onNavigate={onNavigate} />
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading Teams integration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-integration">
      <AdminHeader currentView={currentView} onNavigate={onNavigate} />

      <main className="teams-main">
        <div className="teams-header">
          <div style={{ flex: 1 }}>
            <h1>Microsoft Teams Integration</h1>
            <p className="teams-subtitle">
              Connect SecHire with Microsoft Teams to receive real-time notifications
            </p>
          </div>
          <div className="header-actions">
            <button
              className="cyber-btn info"
              onClick={() => setShowHelpDrawer(true)}
            >
              Setup Guide
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="success-banner">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Main Configuration Card */}
        <div className="config-card">
            {/* Enable/Disable Toggle */}
            <div className="config-section">
              <div className="section-header">
                <h3>Status</h3>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Webhook URL Configuration */}
            <div className="config-section">
              <h3>Webhook URL</h3>
              <p className="section-description">
                Enter your Microsoft Teams incoming webhook URL
              </p>
              <div className="webhook-input-group">
                <input
                  type="text"
                  className="cyber-input"
                  placeholder="https://outlook.office.com/webhook/..."
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
                <button
                  className="cyber-btn secondary"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !webhookUrl}
                >
                  {testingConnection ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="config-section">
              <h3>Notification Preferences</h3>
              <p className="section-description">
                Choose which events should trigger Teams notifications
              </p>

              <div className="notifications-grid">
                <NotificationToggle
                  label="New Application Received"
                  description="Notify when a candidate submits a job application"
                  checked={notifications.newApplication}
                  onChange={() => handleNotificationToggle('newApplication')}
                />

                <NotificationToggle
                  label="Interview Scheduled"
                  description="Notify when an interview is scheduled with a candidate"
                  checked={notifications.interviewScheduled}
                  onChange={() => handleNotificationToggle('interviewScheduled')}
                />

                <NotificationToggle
                  label="Assessment Completed"
                  description="Notify when a candidate completes a technical assessment"
                  checked={notifications.assessmentCompleted}
                  onChange={() => handleNotificationToggle('assessmentCompleted')}
                />

                <NotificationToggle
                  label="Challenge Submitted"
                  description="Notify when a candidate submits a training challenge"
                  checked={notifications.challengeSubmitted}
                  onChange={() => handleNotificationToggle('challengeSubmitted')}
                />

                <NotificationToggle
                  label="System Alerts"
                  description="Notify about system errors and critical issues"
                  checked={notifications.systemAlerts}
                  onChange={() => handleNotificationToggle('systemAlerts')}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="config-actions">
              <button
                className="cyber-btn primary"
                onClick={handleSaveConfiguration}
              >
                Save Configuration
              </button>
            </div>
        </div>

        {/* Microsoft Graph API Configuration Card */}
        <div className="config-card" style={{ marginTop: '2rem' }}>
          <div className="config-section">
            <div className="section-header" style={{ marginBottom: '1.5rem' }}>
              <div>
                <h3>Microsoft Graph API (Direct Messages)</h3>
                <p className="section-description" style={{ marginTop: '0.5rem' }}>
                  Enable personalized direct messages to team members instead of channel notifications
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={graphConfig.enabled}
                  onChange={(e) => setGraphConfig({ ...graphConfig, enabled: e.target.checked })}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">
                  {graphConfig.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>

          {graphConfig.enabled && (
            <>
              {/* Azure AD Credentials */}
              <div className="config-section">
                <h3>Azure AD Application Credentials</h3>
                <p className="section-description">
                  Enter your Azure AD app registration credentials
                </p>

                <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                  <div>
                    <label className="input-label">Tenant ID</label>
                    <input
                      type="text"
                      className="cyber-input"
                      placeholder="12345678-1234-1234-1234-123456789abc"
                      value={graphConfig.tenantId}
                      onChange={(e) => setGraphConfig({ ...graphConfig, tenantId: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="input-label">Client ID (Application ID)</label>
                    <input
                      type="text"
                      className="cyber-input"
                      placeholder="87654321-4321-4321-4321-cba987654321"
                      value={graphConfig.clientId}
                      onChange={(e) => setGraphConfig({ ...graphConfig, clientId: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="input-label">Client Secret</label>
                    <input
                      type="password"
                      className="cyber-input"
                      placeholder="••••••••••••••••••••••••"
                      value={graphConfig.clientSecret}
                      onChange={(e) => setGraphConfig({ ...graphConfig, clientSecret: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="input-label">Organizer Email (for Calendar Events)</label>
                    <input
                      type="email"
                      className="cyber-input"
                      placeholder="hr@yourcompany.com"
                      value={graphConfig.organizerEmail}
                      onChange={(e) => setGraphConfig({ ...graphConfig, organizerEmail: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <button
                    className="cyber-btn secondary"
                    onClick={handleTestGraphConnection}
                    disabled={testingGraphConnection}
                  >
                    {testingGraphConnection ? 'Testing Connection...' : 'Test Graph API Connection'}
                  </button>
                  <button
                    className="cyber-btn info"
                    onClick={() => setShowGraphHelpDrawer(true)}
                    style={{ marginLeft: '1rem' }}
                  >
                    Setup Guide
                  </button>
                </div>
              </div>

              {/* Notification Mode Selection */}
              <div className="config-section">
                <h3>Notification Routing Mode</h3>
                <p className="section-description">
                  Choose how notifications are delivered to your team
                </p>

                <div className="notification-modes" style={{ marginTop: '1rem' }}>
                  <label className={`mode-option ${notificationMode === 'channel' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="notificationMode"
                      value="channel"
                      checked={notificationMode === 'channel'}
                      onChange={(e) => setNotificationMode(e.target.value)}
                    />
                    <div className="mode-content">
                      <h4>Channel Only</h4>
                      <p>All notifications go to the Teams channel (everyone sees everything)</p>
                    </div>
                  </label>

                  <label className={`mode-option ${notificationMode === 'direct' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="notificationMode"
                      value="direct"
                      checked={notificationMode === 'direct'}
                      onChange={(e) => setNotificationMode(e.target.value)}
                    />
                    <div className="mode-content">
                      <h4>Direct Messages Only</h4>
                      <p>Personalized 1-on-1 messages to relevant team members</p>
                    </div>
                  </label>

                  <label className={`mode-option ${notificationMode === 'both' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="notificationMode"
                      value="both"
                      checked={notificationMode === 'both'}
                      onChange={(e) => setNotificationMode(e.target.value)}
                    />
                    <div className="mode-content">
                      <h4>Both (Recommended)</h4>
                      <p>Team transparency + personalized notifications</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Save Graph Config Button */}
              <div className="config-actions">
                <button
                  className="cyber-btn primary"
                  onClick={handleSaveGraphConfig}
                >
                  Save Graph Configuration
                </button>
              </div>
            </>
          )}
        </div>

        {/* Integration Status Card */}
        {isEnabled && (
          <div className="status-card">
            <div className="status-info">
              <div className="status-badge success">
                <span className="status-dot"></span>
                Active
              </div>
              <p>Teams integration is enabled and ready to send notifications</p>
            </div>
          </div>
        )}
      </main>

      {/* Setup Guide Drawer */}
      <Drawer
        isOpen={showHelpDrawer}
        onClose={() => setShowHelpDrawer(false)}
        title="Teams Integration Setup Guide"
        width="lg"
      >
        <div className="setup-guide">
          <section className="guide-section">
            <h3>Step 1: Create an Incoming Webhook in Teams</h3>
            <ol>
              <li>Open Microsoft Teams and navigate to the channel where you want to receive notifications</li>
              <li>Click the three dots (•••) next to the channel name</li>
              <li>Select "Connectors" from the dropdown menu</li>
              <li>Search for "Incoming Webhook" and click "Add"</li>
              <li>Give your webhook a name (e.g., "SecHire Notifications")</li>
              <li>Optionally upload an image</li>
              <li>Click "Create"</li>
              <li>Copy the webhook URL provided</li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Step 2: Configure the Integration</h3>
            <ol>
              <li>Paste the webhook URL in the "Webhook URL" field above</li>
              <li>Click "Test Connection" to verify it works</li>
              <li>Enable the integration using the toggle switch</li>
              <li>Select which notifications you want to receive</li>
              <li>Click "Save Configuration"</li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Notification Types</h3>
            <ul>
              <li><strong>New Application:</strong> When candidates apply for jobs</li>
              <li><strong>Interview Scheduled:</strong> When recruiters schedule interviews</li>
              <li><strong>Assessment Completed:</strong> When candidates finish technical tests</li>
              <li><strong>Challenge Submitted:</strong> When users complete training challenges</li>
              <li><strong>System Alerts:</strong> Critical system errors and issues</li>
            </ul>
          </section>

          <section className="guide-section">
            <h3>Security Notes</h3>
            <ul>
              <li>Webhook URLs are sensitive - keep them private</li>
              <li>Only users with admin permissions can configure integrations</li>
              <li>Notifications contain summary information only, not sensitive data</li>
              <li>You can disable the integration at any time</li>
            </ul>
          </section>
        </div>
      </Drawer>

      {/* Microsoft Graph Setup Guide Drawer */}
      <Drawer
        isOpen={showGraphHelpDrawer}
        onClose={() => setShowGraphHelpDrawer(false)}
        title="Microsoft Graph API Setup Guide"
        width="lg"
      >
        <div className="setup-guide">
          <section className="guide-section">
            <h3>Overview</h3>
            <p>
              Microsoft Graph API enables personalized direct messages to individual team members
              instead of sending all notifications to a shared channel. This allows:
            </p>
            <ul>
              <li>Recruiters to receive DMs only about THEIR job postings</li>
              <li>Interviewers to receive DMs only about THEIR scheduled interviews</li>
              <li>Reduced notification noise for the entire team</li>
              <li>Maintain team transparency with optional channel notifications</li>
            </ul>
          </section>

          <section className="guide-section">
            <h3>Step 1: Register an Azure AD Application</h3>
            <ol>
              <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer">Azure Portal</a></li>
              <li>Navigate to "Azure Active Directory" → "App registrations"</li>
              <li>Click "New registration"</li>
              <li>Enter a name (e.g., "SecHire Teams Integration")</li>
              <li>Select "Accounts in this organizational directory only"</li>
              <li>Click "Register"</li>
              <li>Copy the "Application (client) ID" - this is your <strong>Client ID</strong></li>
              <li>Copy the "Directory (tenant) ID" - this is your <strong>Tenant ID</strong></li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Step 2: Create a Client Secret</h3>
            <ol>
              <li>In your app registration, go to "Certificates & secrets"</li>
              <li>Click "New client secret"</li>
              <li>Add a description (e.g., "SecHire Production")</li>
              <li>Select expiration (recommend 24 months)</li>
              <li>Click "Add"</li>
              <li>Copy the secret <strong>VALUE</strong> immediately - this is your <strong>Client Secret</strong></li>
              <li>Note: You cannot view this secret again after leaving the page</li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Step 3: Configure API Permissions</h3>
            <ol>
              <li>In your app registration, go to "API permissions"</li>
              <li>Click "Add a permission" → "Microsoft Graph" → "Application permissions"</li>
              <li>Add these permissions:
                <ul>
                  <li><strong>Chat.ReadWrite.All</strong> - Send direct messages</li>
                  <li><strong>User.Read.All</strong> - Look up users by email</li>
                  <li><strong>Calendars.ReadWrite</strong> - Create interview meetings</li>
                </ul>
              </li>
              <li>Click "Grant admin consent" (requires Global Admin role)</li>
              <li>Verify all permissions show green checkmarks</li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Step 4: Configure SecHire</h3>
            <ol>
              <li>Enable "Microsoft Graph API (Direct Messages)" toggle above</li>
              <li>Enter the Tenant ID you copied</li>
              <li>Enter the Client ID you copied</li>
              <li>Enter the Client Secret you copied</li>
              <li>Enter an organizer email for calendar events (e.g., hr@yourcompany.com)</li>
              <li>Click "Test Graph API Connection" to verify</li>
              <li>Choose your preferred notification routing mode</li>
              <li>Click "Save Graph Configuration"</li>
            </ol>
          </section>

          <section className="guide-section">
            <h3>Step 5: Link Users to Teams</h3>
            <p>
              For direct messages to work, users need to be linked to their Teams accounts.
              SecHire can automatically look up Teams user IDs from email addresses, or you
              can manually configure them in the People management section.
            </p>
          </section>

          <section className="guide-section">
            <h3>Notification Modes</h3>
            <ul>
              <li><strong>Channel Only:</strong> Current behavior - all notifications to shared channel</li>
              <li><strong>Direct Messages Only:</strong> Personalized 1-on-1 messages, no channel posts</li>
              <li><strong>Both (Recommended):</strong> Team transparency + personalized notifications</li>
            </ul>
          </section>

          <section className="guide-section">
            <h3>Security Notes</h3>
            <ul>
              <li>Client secrets are stored encrypted in the database</li>
              <li>Only admins can configure Graph API settings</li>
              <li>Secrets are never returned to the frontend after saving</li>
              <li>Use least-privilege permissions when possible</li>
              <li>Rotate client secrets before they expire</li>
            </ul>
          </section>

          <section className="guide-section">
            <h3>Need Help?</h3>
            <p>
              For detailed setup instructions with screenshots, see the complete
              Microsoft Graph Setup Guide in the documentation.
            </p>
          </section>
        </div>
      </Drawer>
    </div>
  );
};

/**
 * Notification Toggle Component
 */
const NotificationToggle = ({ label, description, checked, onChange }) => {
  return (
    <div className="notification-toggle">
      <div className="toggle-content">
        <div className="toggle-info">
          <h4>{label}</h4>
          <p>{description}</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
    </div>
  );
};

export default TeamsIntegration;
